import { Shield, Crown, BadgeCheck, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { useCommunities } from '../../hooks/use-communities';
import { Link } from 'react-router-dom';

const roleConfig = {
  owner: {
    icon: Crown,
    label: 'Owner',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-400/10',
    borderColor: 'border-yellow-400/20',
  },
  admin: {
    icon: Shield,
    label: 'Admin',
    color: 'text-red-400',
    bgColor: 'bg-red-400/10',
    borderColor: 'border-red-400/20',
  },
  moderator: {
    icon: BadgeCheck,
    label: 'Moderator',
    color: 'text-blue-400',
    bgColor: 'bg-blue-400/10',
    borderColor: 'border-blue-400/20',
  },
};

export default function CommunityStaffModal({
  communityOid,
  open,
  onOpenChange,
}) {
  const { useGetCommunityStaff } = useCommunities();
  const { data: staff, isLoading } = useGetCommunityStaff(communityOid);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Shield className="w-5 h-5 text-violet-400" />
            Community Staff
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
            </div>
          ) : staff && staff.length > 0 ? (
            staff.map((member, index) => {
              const roleInfo = roleConfig[member.role] || roleConfig.moderator;
              const RoleIcon = roleInfo.icon;

              return (
                <Link
                  key={index}
                  to={`/profile/${member.user?.creator || member.user?.oid}`}
                  className={`block p-4 rounded-lg border ${roleInfo.borderColor} ${roleInfo.bgColor} hover:bg-opacity-20 transition-all`}
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12 border-2 border-zinc-700">
                      <AvatarImage src={member.user?.avatar} />
                      <AvatarFallback className="bg-gradient-to-br from-violet-500 to-cyan-500 text-white font-bold">
                        {member.user?.username?.charAt(0)?.toUpperCase() ||
                          member.user?.creator?.slice(0, 2)}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-white truncate">
                          {member.user?.username ||
                            `${member.user?.creator?.slice(
                              0,
                              6
                            )}...${member.user?.creator?.slice(-4)}`}
                        </p>
                        {member.user?.isVerified && (
                          <BadgeCheck className="w-4 h-4 text-blue-400 flex-shrink-0" />
                        )}
                      </div>
                      {member.user?.bio && (
                        <p className="text-sm text-zinc-400 truncate">
                          {member.user.bio}
                        </p>
                      )}
                    </div>

                    <Badge
                      className={`${roleInfo.bgColor} ${roleInfo.color} border ${roleInfo.borderColor} flex items-center gap-1`}
                    >
                      <RoleIcon className="w-3 h-3" />
                      {roleInfo.label}
                    </Badge>
                  </div>

                  {member.joinedAt && (
                    <p className="text-xs text-zinc-500 mt-2">
                      Joined {new Date(member.joinedAt).toLocaleDateString()}
                    </p>
                  )}
                </Link>
              );
            })
          ) : (
            <div className="text-center py-8 text-zinc-400">
              <Shield className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No staff members found</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
